import React, { useLayoutEffect, useRef, useState } from "react";
import Text from "@/components/ui/text";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger
} from "@/components/ui/tooltip";
import { cn } from "@/utils/utils";
import { useTranslation } from "react-i18next";
import { Bullet } from "@dreamhub/designsystem/icons";
import IconContainer from "@/components/ui/icon-container";

const GAP_PX = 16;
const MORE_POLYGON_COLORS = [
  "var(--charts-pink-strong)",
  "var(--charts-teal-strong)",
  "var(--charts-emerald-strong)",
  "var(--charts-gold-strong)"
];

interface LegendItem {
  key: string;
  label: string;
  color: string;
}

interface Props {
  items: LegendItem[];
  className?: string;
}

const Item = ({ item }: { item: LegendItem }) => (
  <div className="flex items-center gap-1.5 shrink-0">
    <IconContainer className="size-[10px]" style={{ color: item.color }}>
      <Bullet />
    </IconContainer>
    <Text variant="smBody" className="text-secondary">
      {item.label}
    </Text>
  </div>
);

const MorePill = ({ label }: { label: string }) => (
  <div className="flex items-center gap-1.5 shrink-0">
    <div className="grid grid-cols-2 gap-0.5">
      {MORE_POLYGON_COLORS.map((color, index) => (
        <IconContainer
          key={`more-${index}`}
          className="size-[6px]"
          style={{ color }}
        >
          <Bullet />
        </IconContainer>
      ))}
    </div>
    <Text variant="smBody" className="text-secondary">
      {label}
    </Text>
  </div>
);

const HiddenItemsTooltip = ({ items }: { items: LegendItem[] }) => (
  <div className="flex flex-col gap-1.5">
    {items.map((item) => (
      <div key={item.key} className="flex items-center gap-2">
        <IconContainer className="size-[8px]" style={{ color: item.color }}>
          <Bullet />
        </IconContainer>
        <Text variant="smBody" className="text-primary">
          {item.label}
        </Text>
      </div>
    ))}
  </div>
);

const LegendWithMore: React.FC<Props> = ({ items, className }) => {
  const { t } = useTranslation();
  const containerRef = useRef<HTMLDivElement>(null);
  const measureRef = useRef<HTMLDivElement>(null);
  const [visibleCount, setVisibleCount] = useState(items.length);
  const moreLabel = t("more");

  useLayoutEffect(() => {
    const container = containerRef.current;
    const measure = measureRef.current;
    if (!container || !measure) return;

    const calculate = () => {
      const containerWidth = container.clientWidth;
      // Skip when the container hasn't laid out yet (hidden ancestor,
      // deferred mount). Without this, a 0-width measurement would collapse
      // the visible row to only the More pill until the next ResizeObserver
      // tick fires.
      if (containerWidth === 0) return;
      const children = Array.from(measure.children) as HTMLElement[];
      const itemEls = children.slice(0, items.length);
      const moreEl = children[items.length];
      const moreWidth = moreEl?.offsetWidth ?? 0;

      let totalAll = 0;
      for (let i = 0; i < itemEls.length; i++) {
        if (i > 0) totalAll += GAP_PX;
        totalAll += itemEls[i].offsetWidth;
      }
      if (totalAll <= containerWidth) {
        setVisibleCount(items.length);
        return;
      }

      let total = 0;
      let count = 0;
      for (let i = 0; i < itemEls.length; i++) {
        const candidate =
          total +
          (i > 0 ? GAP_PX : 0) +
          itemEls[i].offsetWidth +
          GAP_PX +
          moreWidth;
        if (candidate <= containerWidth) {
          total += (i > 0 ? GAP_PX : 0) + itemEls[i].offsetWidth;
          count++;
        } else {
          break;
        }
      }
      setVisibleCount(count);
    };

    const observer = new ResizeObserver(calculate);
    observer.observe(container);
    calculate();
    return () => observer.disconnect();
    // moreLabel as dep so a locale switch re-measures (the More-pill width
    // changes without a window resize event).
  }, [items, moreLabel]);

  const hidden = items.slice(visibleCount);

  return (
    <div className={cn("relative w-full mt-4", className)}>
      <div
        ref={measureRef}
        aria-hidden
        className="absolute invisible pointer-events-none flex gap-x-4 left-0 top-0"
      >
        {items.map((item) => (
          <Item key={`measure-${item.key}`} item={item} />
        ))}
        <MorePill label={moreLabel} />
      </div>
      <div
        ref={containerRef}
        className="flex justify-center gap-x-4 overflow-hidden whitespace-nowrap"
      >
        {items.slice(0, visibleCount).map((item) => (
          <Item key={item.key} item={item} />
        ))}
        {hidden.length > 0 && (
          <Tooltip>
            <TooltipTrigger asChild>
              <button
                type="button"
                className="cursor-default focus-visible:outline-none"
                aria-label={moreLabel}
              >
                <MorePill label={moreLabel} />
              </button>
            </TooltipTrigger>
            <TooltipContent enableMarkdown={false} side="top" sideOffset={6}>
              <HiddenItemsTooltip items={hidden} />
            </TooltipContent>
          </Tooltip>
        )}
      </div>
    </div>
  );
};

export default LegendWithMore;
